import { Injectable } from '@angular/core';
import { loginRequest } from '../app-config';
import { MsalService } from '@azure/msal-angular';
import { Observable, of, Subject } from 'rxjs';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';

@Injectable({ providedIn: 'root' })
export class AzureLoginService {
  public static readonly ACCESS_TOKEN_NAME = 'kairos_access_token';
  public static readonly EXPIRES_IN_OFFSET_SECONDS = 3;
  public LOGIN_ERROR_MESSAGE = 'The user name or password is incorrect. Please try again.';
  public headers = new Headers({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  public accessToken: any;
  public userLoggedIn: boolean;
  public currentUser: string;
  public fullName: string;
  public userPhoto;
  public authorityInstance;
  public homeAccountIdentifier: string;
  public emailAddress: string;
  public accountId: string;
  public accountIdBase64: string;
  public roleName: string;
  public servicerNumberLink: any = [];
  public dealerNumberLink: any = [];
  public workQueueLink: any = [];
  public firstName: string;
  public lastName: string;
  public displayName: string;
  public rolePermissions: any[] = [];
  rolePermissionsLoaded = false;

  /**
   * Fires after `setRolePermissions` so the CRM sidebar can re-run `validateSideMenu` / `validateSearch`
   * once role-permission rows are available (avoids intermittent empty sub-menus at startup).
   */
  readonly rolePermissionsUpdated$ = new Subject<void>();
  constructor(private azureService: MsalService) {
    this.accessToken = null;
  }

  /** Refresh account fields from the active MSAL account (safe to call after login/token refresh). */
  syncAccountFromMsal(): void {
    try {
      const accountDetails: any = this.azureService.instance.getActiveAccount();
      if (!accountDetails) {
        return;
      }

      const emails = accountDetails.idTokenClaims?.emails;
      if (emails?.length > 0) {
        this.emailAddress = emails[0];
      }
      this.displayName = accountDetails.name;
      this.homeAccountIdentifier = accountDetails.homeAccountIdentifier;
      this.accountId = accountDetails.localAccountId;
      if (accountDetails.accountIdentifier) {
        this.accountIdBase64 = btoa(accountDetails.accountIdentifier);
      }
    } catch {
      /* MSAL may not be initialized yet during early DI */
    }
  }

  /** Returns the Azure user id, syncing from MSAL when not yet cached. */
  getAccountIdValue(): string {
    if (!this.accountId) {
      this.syncAccountFromMsal();
    }
    return this.accountId;
  }

  public setAccessToken(token: string) {
    // const newToken: any = {
    //   token: token.accessToken || token.token,
    //   expiresIn: token.expiresIn,
    //   refreshToken: token.refreshToken,
    //   personnelId: token.PersonnelId || token.personnelId,
    //   currentUser: token.currentUser,
    //   firstName: token.FirstName || token.firstName,
    //   lastName: token.LastName || token.lastName,
    //   isSupervisor: token.IsSupervisor || token.isSupervisor,
    //   authorizations: token.authorizations,
    // };

    // localStorage.setItem(AzureLoginService.ACCESS_TOKEN_NAME, JSON.stringify(newToken));
    this.accessToken = token;
    this.userLoggedIn = true;
    this.syncAccountFromMsal();
  }

  public getAccessToken() {
    return this.accessToken;
  }

  public getFullName(): Observable<string> {
    return of(`${this.firstName} ${this.lastName}`);
  }

  public getUserName(): Observable<string> {
    return of(this.displayName);
  }

  public getUserPhoto(): Observable<string> {
    return of(this.userPhoto);
  }

  public checkTokenExpiry() {
    const accountDetails = this.azureService.instance.getActiveAccount();
    console.log('Account Details: ' + JSON.stringify(accountDetails));
  }

  public async getAccessTokenAsync() {
    const accounts = this.azureService.instance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    
    // Get active account or use first account
    let account = this.azureService.instance.getActiveAccount();
    if (!account) {
      account = accounts[0];
      this.azureService.instance.setActiveAccount(account);
    }
    
    try {
      // Use instance.acquireTokenSilent with account parameter to avoid no_account_error
      const result = await this.azureService.instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return result;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        return null;
      }
      const code = (err as { errorCode?: string })?.errorCode;
      if (code === 'monitor_window_timeout' || code === 'iframe_closed_early') {
        return null;
      }
      // Catch BrowserAuthError: no_account_error
      if ((err as any)?.errorCode === 'no_account_error' || 
          (err as any)?.name === 'BrowserAuthError' && 
          String(err).includes('no_account_error')) {
        return null;
      }
      throw err;
    }
  }

  public refreshAccessToken() {
    const accounts = this.azureService.instance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return;
    }
    
    // Get active account or use first account
    let account = this.azureService.instance.getActiveAccount();
    if (!account) {
      account = accounts[0];
      this.azureService.instance.setActiveAccount(account);
    }
    
    // Use instance.acquireTokenSilent with account parameter to avoid no_account_error
    this.azureService.instance.acquireTokenSilent({
      ...loginRequest,
      account,
    }).then((result: AuthenticationResult) => {
      this.accessToken = result.idToken;
    }).catch((err) => {
      if (err instanceof InteractionRequiredAuthError) {
        // Token refresh failed - silently redirect to login (like session timeout)
        if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
          window.location.href = '/';
        }
        return;
      }
      const code = (err as { errorCode?: string })?.errorCode;
      // Transient silent-iframe failures: keep existing token; do not interrupt the session (idle ping path).
      if (code === 'monitor_window_timeout' || code === 'iframe_closed_early') {
        return;
      }
      // Catch BrowserAuthError: no_account_error
      if ((err as any)?.errorCode === 'no_account_error' || 
          (err as any)?.name === 'BrowserAuthError' && 
          String(err).includes('no_account_error')) {
        if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
          window.location.href = '/';
        }
      }
    });
  }

  public getAccountId(): Observable<string> {
    return of(this.accountId);
  }

  setRolePermissions(perms: any[]) {
    this.rolePermissions = Array.isArray(perms) ? perms : [];
    this.rolePermissionsLoaded = true;
    this.rolePermissionsUpdated$.next();
  }

  resetRolePermissions(): void {
    this.rolePermissions = [];
    this.rolePermissionsLoaded = false;
  }

  async asyncTokenLookup() {
    const account = this.azureService.instance.getActiveAccount();
    if (account) {
      try {
        const result = await this.azureService.instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        return result.idToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          return null;
        }
        const code = (err as { errorCode?: string })?.errorCode;
        if (code === 'monitor_window_timeout' || code === 'iframe_closed_early') {
          return null;
        }
        throw err;
      }
    }
    return null;
  }
}
